(function () {
  'use strict';

  var DEFAULT_STORAGE_KEY = 'preworn:wishlist';
  var undoTimer = null;
  var lastRemoved = null;
  var localChangeInProgress = false;

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#096;');
  }

  function safeUrl(value, fallback) {
    var raw = String(value || '').trim();
    var safeFallback = typeof fallback === 'string' ? fallback : '#';
    if (!raw) return safeFallback;
    if (/^(?:https?:)?\/\//i.test(raw) || raw.charAt(0) === '/') return raw;
    return safeFallback;
  }

  function normaliseItem(item, index) {
    if (!item || typeof item !== 'object') return null;

    var handle = String(item.handle || '').trim();
    var url = safeUrl(item.url, handle ? '/products/' + encodeURIComponent(handle) : '#');
    var id = String(item.id || handle || url || index);

    return {
      id: id,
      handle: handle,
      url: url,
      vendor: String(item.vendor || 'PREWORN'),
      title: String(item.title || 'One-off preloved piece'),
      meta: String(item.meta || ''),
      image: safeUrl(item.image, ''),
      price: String(item.price || ''),
      originalPrice: String(item.originalPrice || ''),
      saving: String(item.saving || ''),
      discountPercent: Number(item.discountPercent) || 0,
      category: String(item.category || ''),
      collectionHandle: String(item.collectionHandle || ''),
      collectionTitle: String(item.collectionTitle || '')
    };
  }

  function readItems(storageKey) {
    try {
      var stored = window.localStorage.getItem(storageKey);
      var parsed = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) return [];

      var seen = {};
      return parsed.map(normaliseItem).filter(function (item) {
        if (!item || seen[item.id]) return false;
        seen[item.id] = true;
        return true;
      });
    } catch (error) {
      console.warn('[Preworn Wishlist] Saved items could not be read.', error);
      return [];
    }
  }

  function writeItems(storageKey, items) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(items));
      return true;
    } catch (error) {
      console.warn('[Preworn Wishlist] Saved items could not be updated.', error);
      return false;
    }
  }

  function dispatchChange(item, saved, count) {
    if (typeof window.CustomEvent !== 'function') return;
    localChangeInProgress = true;
    try {
      window.dispatchEvent(new CustomEvent('preworn:wishlist-change', {
        detail: { item: item, saved: saved, count: count }
      }));
    } finally {
      localChangeInProgress = false;
    }
  }

  function priceMarkup(item) {
    if (!item.price && !item.originalPrice) {
      return '<div class="pw-card__price"><span class="pw-card__current">View price</span></div>';
    }

    var markup = '<div class="pw-card__price">';
    if (item.originalPrice && item.originalPrice !== item.price) {
      markup += '<span class="pw-card__original">' + escapeHtml(item.originalPrice) + '</span>';
    }
    if (item.price) markup += '<span class="pw-card__current">' + escapeHtml(item.price) + '</span>';
    markup += '</div>';
    if (item.saving) markup += '<div class="pw-card__saving">Save ' + escapeHtml(item.saving) + '</div>';
    return markup;
  }

  function discountMarkup(item) {
    var discount = Math.round(Number(item.discountPercent) || 0);

    if (discount <= 0 && item.originalPrice && item.price) {
      var original = Number(String(item.originalPrice).replace(/[^0-9.-]/g, ''));
      var current = Number(String(item.price).replace(/[^0-9.-]/g, ''));
      if (original > 0 && current >= 0 && current < original) {
        discount = Math.round(((original - current) / original) * 100);
      }
    }

    if (discount <= 0) return '';

    return (
      '<div class="pw-card__discount pw-discount--' + discount + '" aria-label="' + discount + '% off">' +
        '<span>' + discount + '%</span><small>OFF</small>' +
      '</div>'
    );
  }

  function cardMarkup(item) {
    var title = item.vendor ? item.vendor + ' ' + item.title : item.title;
    var canAddToCart = !!item.handle || /\/products\//i.test(item.url);
    var image = item.image
      ? '<img src="' + escapeAttr(item.image) + '" alt="' + escapeAttr(title) + '" loading="lazy">'
      : '<div class="pw-card__no-image">No image</div>';

    return (
      '<li class="pw-card" data-pw-wishlist-card data-item-id="' + escapeAttr(item.id) + '">' +
        '<div class="pw-card__media">' +
          '<a class="pw-card__image-link" href="' + escapeAttr(item.url) + '" aria-label="View ' + escapeAttr(title) + '">' +
            image + discountMarkup(item) +
          '</a>' +
          '<div class="pw-card__actions">' +
            '<button class="pw-card__cart" type="button" data-pw-add-cart data-product-handle="' + escapeAttr(item.handle) + '" data-product-url="' + escapeAttr(item.url) + '"' +
              (canAddToCart ? '' : ' disabled') +
              ' aria-label="' + (canAddToCart ? 'Add ' + escapeAttr(title) + ' to basket' : 'Product unavailable') + '"' +
              ' title="' + (canAddToCart ? 'Add to basket' : 'Product unavailable') + '">' +
              '<svg class="pw-card__cart-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M5.5 8.5h13l1 12h-15l1-12Z"></path><path d="M9 9V6.5a3 3 0 0 1 6 0V9"></path><path d="M12 12v5M9.5 14.5h5"></path></svg>' +
              '<svg class="pw-card__cart-check" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m6.5 12.5 3.5 3.5 7.5-8"></path></svg>' +
            '</button>' +
            '<button class="pw-card__wishlist is-saved" type="button" data-pw-wishlist-remove data-item-id="' + escapeAttr(item.id) + '" aria-pressed="true" aria-label="Remove ' + escapeAttr(title) + ' from wishlist" title="Remove from wishlist">' +
              '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"></path></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
        '<a class="pw-card__link" href="' + escapeAttr(item.url) + '">' +
          '<div class="pw-card__body">' +
            '<div class="pw-card__vendor">' + escapeHtml(item.vendor) + '</div>' +
            '<div class="pw-card__title">' + escapeHtml(item.title) + '</div>' +
            (item.meta ? '<div class="pw-card__meta">' + escapeHtml(item.meta) + '</div>' : '') +
            (item.handle ? '<div class="pwdemand-slot pwdemand-slot--card" data-pw-demand data-product-handle="' + escapeAttr(item.handle) + '" data-pw-demand-context="card" hidden></div>' : '') +
            priceMarkup(item) +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  function categoryLabel(item) {
    var label = String(item.category || '').trim();
    if (!label) label = String(item.title || '').trim();
    return label || 'Other finds';
  }

  function pluralCategory(label, count) {
    if (count === 1 || /(?:jeans|trousers|shorts|leggings|clothes)$/i.test(label)) return label;
    if (/[^s]s$/i.test(label)) return label;
    if (/[^aeiou]y$/i.test(label)) return label.slice(0, -1) + 'ies';
    if (/(?:s|x|z|ch|sh)$/i.test(label)) return label + 'es';
    return label + 's';
  }

  function renderCategoryFilters(root, items) {
    var target = root.querySelector('[data-pw-wishlist-collections]');
    if (!target) return;

    var counts = {};
    for (var i = 0; i < items.length; i++) {
      var label = categoryLabel(items[i]);
      counts[label] = (counts[label] || 0) + 1;
    }

    var categories = Object.keys(counts).sort(function (a, b) {
      return counts[b] - counts[a] || a.localeCompare(b);
    });

    if (categories.length < 2) {
      target.hidden = true;
      target.innerHTML = '';
      root._pwWishlistCategory = 'all';
      return;
    }

    var selected = root._pwWishlistCategory || 'all';
    if (selected !== 'all' && !counts[selected]) selected = 'all';
    root._pwWishlistCategory = selected;

    var markup = '<span class="pwwish__collections-label">COLLECTIONS</span>' +
      '<button type="button" class="pwwish__collection-chip' +
      (selected === 'all' ? ' is-active' : '') + '" data-pw-wishlist-category="all" aria-pressed="' +
      (selected === 'all' ? 'true' : 'false') + '">ALL <span>' + items.length + '</span></button>';

    for (var c = 0; c < categories.length; c++) {
      var category = categories[c];
      var active = selected === category;
      markup += '<button type="button" class="pwwish__collection-chip' +
        (active ? ' is-active' : '') + '" data-pw-wishlist-category="' + escapeAttr(category) +
        '" aria-pressed="' + (active ? 'true' : 'false') + '">' +
        escapeHtml(pluralCategory(category, counts[category])).toUpperCase() +
        ' <span>' + counts[category] + '</span></button>';
    }

    target.innerHTML = markup;
    target.hidden = false;
  }

  function money(amount, currency) {
    var value = Number(amount);
    if (!isFinite(value)) return '';

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(value);
    } catch (error) {
      return '£' + value.toFixed(2);
    }
  }

  function tieredPrice(rawPrice) {
    var original = Number(rawPrice);
    if (!isFinite(original) || original <= 0) return null;

    var percent = 55;
    var multiplier = .45;
    if (original <= 5) {
      percent = 10;
      multiplier = .9;
    } else if (original <= 10) {
      percent = 20;
      multiplier = .8;
    } else if (original <= 15) {
      percent = 30;
      multiplier = .7;
    } else if (original <= 20) {
      percent = 40;
      multiplier = .6;
    } else if (original < 30) {
      percent = 45;
      multiplier = .55;
    }

    return {
      original: original,
      discounted: Math.round(original * multiplier * 100) / 100,
      percent: percent
    };
  }

  function productCategory(product) {
    var tags = Array.isArray(product.tags) ? product.tags : [];
    for (var i = 0; i < tags.length; i++) {
      if (/^Type_/i.test(tags[i])) return String(tags[i]).replace(/^Type_/i, '').trim();
    }

    return String(product.type || '').replace(/^(?:Womens|Mens|Girls|Boys|Baby)\s+/i, '').trim();
  }

  function productImage(product) {
    var image = String(product.featured_image || '').trim();
    if (image.indexOf('//') === 0) return 'https:' + image;
    return safeUrl(image, '');
  }

  function enrichmentPatch(item, product) {
    var price = tieredPrice(Number(product.price || 0) / 100);
    var patch = {
      category: productCategory(product),
      image: item.image || productImage(product),
      vendor: item.vendor || String(product.vendor || ''),
      url: item.url === '#' ? safeUrl(product.url, item.url) : item.url
    };

    if (price) {
      patch.price = money(price.discounted, 'GBP');
      patch.originalPrice = money(price.original, 'GBP');
      patch.saving = money(price.original - price.discounted, 'GBP');
      patch.discountPercent = price.percent;
    }

    return patch;
  }

  function enrichItems(root) {
    if (root.getAttribute('data-pw-wishlist-enriched') === 'true') return;
    root.setAttribute('data-pw-wishlist-enriched', 'true');

    var storageKey = root.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY;
    var items = readItems(storageKey);
    var candidates = items.filter(function (item) {
      return item.handle && (!item.discountPercent || !item.originalPrice || !item.category);
    });
    if (!candidates.length || typeof window.fetch !== 'function') return;
    var storeOrigin = String(root.getAttribute('data-store-origin') || '').replace(/\/$/, '');

    Promise.all(candidates.map(function (item) {
      return window.fetch(storeOrigin + '/products/' + encodeURIComponent(item.handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Product lookup failed');
        return response.json();
      }).then(function (product) {
        return { id: item.id, patch: enrichmentPatch(item, product) };
      }).catch(function () {
        return null;
      });
    })).then(function (results) {
      var patches = {};
      for (var r = 0; r < results.length; r++) {
        if (results[r]) patches[results[r].id] = results[r].patch;
      }

      var currentItems = readItems(storageKey);
      var changed = false;
      for (var i = 0; i < currentItems.length; i++) {
        var patch = patches[currentItems[i].id];
        if (!patch) continue;
        Object.assign(currentItems[i], patch);
        changed = true;
      }

      if (changed && writeItems(storageKey, currentItems)) render(root);
    });
  }

  function updateCount(root, count) {
    var heroCount = root.querySelector('[data-pw-wishlist-hero-count]');
    var inlineCount = root.querySelector('[data-pw-wishlist-inline-count]');
    if (heroCount) heroCount.textContent = count < 10 ? '0' + count : String(count);
    if (inlineCount) inlineCount.textContent = '(' + count + ')';
  }

  function render(root) {
    var storageKey = root.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY;
    var items = readItems(storageKey);
    var grid = root.querySelector('[data-pw-wishlist-grid]');
    var saved = root.querySelector('[data-pw-wishlist-saved]');
    var empty = root.querySelector('[data-pw-wishlist-empty]');
    var loading = root.querySelector('[data-pw-wishlist-loading]');

    updateCount(root, items.length);
    renderCategoryFilters(root, items);
    var selectedCategory = root._pwWishlistCategory || 'all';
    if (loading) loading.hidden = true;

    if (!items.length) {
      if (grid) grid.innerHTML = '';
      if (saved) saved.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    var visibleItems = selectedCategory === 'all'
      ? items
      : items.filter(function (item) { return categoryLabel(item) === selectedCategory; });
    if (grid) grid.innerHTML = visibleItems.map(cardMarkup).join('');
    if (saved) saved.hidden = false;
    if (empty) empty.hidden = true;
  }

  function hideToast(root) {
    var toast = root.querySelector('[data-pw-wishlist-toast]');
    if (toast) toast.hidden = true;
    if (undoTimer) window.clearTimeout(undoTimer);
    undoTimer = null;
  }

  function showToast(root) {
    var toast = root.querySelector('[data-pw-wishlist-toast]');
    if (!toast) return;
    toast.hidden = false;
    if (undoTimer) window.clearTimeout(undoTimer);
    undoTimer = window.setTimeout(function () {
      hideToast(root);
      lastRemoved = null;
    }, 6000);
  }

  function removeItem(root, id, card) {
    var storageKey = root.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY;
    var items = readItems(storageKey);
    var index = items.findIndex(function (item) { return item.id === id; });
    if (index === -1) return;

    lastRemoved = { item: items[index], index: index };
    items.splice(index, 1);
    if (!writeItems(storageKey, items)) return;

    if (card) card.classList.add('is-removing');
    window.setTimeout(function () { render(root); }, card ? 210 : 0);
    dispatchChange(lastRemoved.item, false, items.length);
    showToast(root);
  }

  function undoRemove(root) {
    if (!lastRemoved) return;
    var storageKey = root.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY;
    var items = readItems(storageKey);
    var exists = items.some(function (item) { return item.id === lastRemoved.item.id; });

    if (!exists) {
      items.splice(Math.min(lastRemoved.index, items.length), 0, lastRemoved.item);
      if (writeItems(storageKey, items)) {
        dispatchChange(lastRemoved.item, true, items.length);
      }
    }

    lastRemoved = null;
    hideToast(root);
    render(root);
  }

  function bind(root) {
    if (root.getAttribute('data-pw-wishlist-ready') === 'true') return;
    root.setAttribute('data-pw-wishlist-ready', 'true');

    root.addEventListener('click', function (event) {
      var removeButton = event.target.closest('[data-pw-wishlist-remove]');
      if (removeButton) {
        var card = removeButton.closest('[data-pw-wishlist-card]');
        removeItem(root, removeButton.getAttribute('data-item-id'), card);
        return;
      }

      if (event.target.closest('[data-pw-wishlist-undo]')) undoRemove(root);

      var categoryButton = event.target.closest('[data-pw-wishlist-category]');
      if (categoryButton) {
        root._pwWishlistCategory = categoryButton.getAttribute('data-pw-wishlist-category') || 'all';
        render(root);
      }
    });

    window.addEventListener('storage', function (event) {
      var storageKey = root.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY;
      if (event.key === storageKey) render(root);
    });

    window.addEventListener('preworn:wishlist-change', function () {
      if (localChangeInProgress) return;
      render(root);
    });

    window.addEventListener('pageshow', function () {
      render(root);
    });

    render(root);
    enrichItems(root);
  }

  function boot(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = context.querySelectorAll('[data-pw-wishlist-page]');
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
})();
